import { OnPremNetworkOutput, OnPremStorageOutput, StorageAnnualCost, StorageVolume } from '@amzn/awscat-portfolio-assessment-types';
import { Box, Table, TableProps } from '@amzn/awsui-components-react';

import { constants } from '../../../../common/Localization';
import { formatCurrencyNoDecimals, formatNumberAsString } from '../../../../common/LocalizationHelper';
import AWSCATTable from '../../../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../../../common/table/types';
import { getCell } from '../../../common/awsui-table-helper';
import { CashFlowData } from '../../common/types';

interface StorageSummaryItem {
    type: string;
    totalStorage: number | null;
    totalUsableStorage: number | null;
    cost: number | null;
}
const storageSummaryTableCols = (currency: string): TableProps.ColumnDefinition<StorageSummaryItem>[] => [{
    id: 'type',
    header: constants.TCO_PAGE_SUMMARY_TYPE,
    cell: (item: StorageSummaryItem) => (<Box float='left'><strong>{item.type}</strong></Box>),
}, {
    id: 'totalStorage',
    header: <Box float='right'>{constants.ON_PREM_STORAGE_TOTAL}</Box>,
    cell: (item: StorageSummaryItem) => (
        <Box float='right'>{item.totalStorage != null ? formatNumberAsString(item.totalStorage, { maximumFractionDigits: 0 }) + ' GB' : '-'}</Box>
    ),
}, {
    id: 'totalUsableStorage',
    header: <Box float='right'>{constants.ON_PREM_STORAGE_USAGE_TOTAL}</Box>,
    cell: (item: StorageSummaryItem) => (
        <Box float='right'>{item.totalUsableStorage != null ? formatNumberAsString(item.totalUsableStorage, { maximumFractionDigits: 0 }) + ' GB' : '-'}</Box>
    ),
}, {
    id: 'cost',
    header: <Box float='right'>{constants.COST}</Box>,
    cell: (item: StorageSummaryItem) => (
        <Box float='right'>{item.cost != null ? formatCurrencyNoDecimals(item.cost, currency) : '-'}</Box>
    ),
}];

export const buildStorageSummaryTable = (storage: OnPremStorageOutput, currency: string): JSX.Element => {
    const storageSummaryItems: StorageSummaryItem[] = [{
        type: constants.ON_PREM_PHYSICAL_SERVER_STORAGE,
        totalStorage: storage.summaryByType.serverVolumes?.rawSizeInGB ?? null,
        totalUsableStorage: storage.summaryByType.serverVolumes?.usableSizeInGB ?? null,
        cost: storage.summaryByType.serverVolumes?.totalCosts ?? null,
    }, {
        type: constants.ON_PREM_VIRTUAL_SERVER_STORAGE,
        totalStorage: storage.summaryByType.ServerSAN?.rawSizeInGB ?? null,
        totalUsableStorage: storage.summaryByType.ServerSAN?.usableSizeInGB ?? null,
        cost: storage.summaryByType.ServerSAN?.totalCosts ?? null,
    }, {
        type: constants.ON_PREM_PHYSICAL_DATABASE_STORAGE,
        totalStorage: storage.summaryByType.databaseVolumes?.rawSizeInGB ?? null,
        totalUsableStorage: storage.summaryByType.databaseVolumes?.usableSizeInGB ?? null,
        cost: storage.summaryByType.databaseVolumes?.totalCosts ?? null,
    }, {
        type: constants.ON_PREM_VIRTUAL_DATABASE_STORAGE,
        totalStorage: storage.summaryByType.DatabaseSAN?.rawSizeInGB ?? null,
        totalUsableStorage: storage.summaryByType.DatabaseSAN?.usableSizeInGB ?? null,
        cost: storage.summaryByType.DatabaseSAN?.totalCosts ?? null,
    }, {
        type: constants.ON_PREM_STORAGE_SAN,
        totalStorage: storage.summaryByType.SAN?.rawSizeInGB ?? null,
        totalUsableStorage: storage.summaryByType.SAN?.usableSizeInGB ?? null,
        cost: storage.summaryByType.SAN?.totalCosts ?? null,
    }, {
        type: constants.ON_PREM_STORAGE_NAS,
        totalStorage: storage.summaryByType.NAS?.rawSizeInGB ?? null,
        totalUsableStorage: storage.summaryByType.NAS?.usableSizeInGB ?? null,
        cost: storage.summaryByType.NAS?.totalCosts ?? null,
    }, {
        type: constants.ON_PREM_STORAGE_OBJECT,
        totalStorage: storage.summaryByType.Object?.rawSizeInGB ?? null,
        totalUsableStorage: storage.summaryByType.Object?.usableSizeInGB ?? null,
        cost: storage.summaryByType.Object?.totalCosts ?? null,
    }];

    return (
        <Table
            columnDefinitions={storageSummaryTableCols(currency)}
            items={storageSummaryItems}
            sortingDisabled
        />
    );
};

interface StorageBackupSummaryItem {
    type: string;
    totalBackupSize: number;
    tape: number;
    tapeCartridges: number;
    cost: number;
}

const storageBackupSummaryTableCols = (currency: string): TableProps.ColumnDefinition<StorageBackupSummaryItem>[] => [{
    id: 'type',
    header: constants.TCO_PAGE_SUMMARY_TYPE,
    cell: (item: StorageBackupSummaryItem) => (<Box float='left'><strong>{item.type}</strong></Box>),
}, {
    id: 'totalBackupSize',
    header: <Box float='right'>{constants.ON_PREM_STORAGE_BACK_UP_TOTAL_SIZE}</Box>,
    cell: (item: StorageBackupSummaryItem) => (
        <Box float='right'>{item.totalBackupSize != null ? formatNumberAsString(item.totalBackupSize, { maximumFractionDigits: 0 }) + ' GB' : '-'}</Box>
    ),
}, {
    id: 'tapesRequired',
    header: <Box float='right'>{constants.ON_PREM_TAPES_REQUIRED}</Box>,
    cell: (item: StorageBackupSummaryItem) => (<Box float='right'>{item.tape}</Box>),
}, {
    id: 'tapeCartridegeRequired',
    header: <Box float='right'>{constants.ON_PREM_TAPES_CARTRIDGES_REQUIRED}</Box>,
    cell: (item: StorageBackupSummaryItem) => (<Box float='right'>{item.tapeCartridges}</Box>),
}, {
    id: 'cost',
    header: <Box float='right'>{constants.COST}</Box>,
    cell: (item: StorageBackupSummaryItem) => (
        <Box float='right'>{item.cost != null ? formatCurrencyNoDecimals(item.cost, currency) : '-'}</Box>
    ),
}];

export const buildStorageBackupSummaryTable = (storage: OnPremStorageOutput, currency: string): JSX.Element => {
    const storageBackupSummaryItems: StorageBackupSummaryItem[] = [];
    if (storage.backupSummary.serverStorageBackup) {
        const backup = storage.backupSummary.serverStorageBackup;
        storageBackupSummaryItems.push({
            type: constants.STORAGE_SERVER,
            totalBackupSize: backup.totalBackupSize,
            tape: backup.numberTapesRequired,
            tapeCartridges: backup.numberTapeCartridgesRequired,
            cost: backup.backupCosts,
        });
    }
    if (storage.backupSummary.databaseStorageBackup) {
        const backup = storage.backupSummary.databaseStorageBackup;
        storageBackupSummaryItems.push({
            type: constants.STORAGE_DATABASE,
            totalBackupSize: backup.totalBackupSize,
            tape: backup.numberTapesRequired,
            tapeCartridges: backup.numberTapeCartridgesRequired,
            cost: backup.backupCosts,
        });
    }
    if (storage.backupSummary.sharedStorageBackup) {
        const backup = storage.backupSummary.sharedStorageBackup;
        storageBackupSummaryItems.push({
            type: constants.STORAGE_SHARED,
            totalBackupSize: backup.totalBackupSize,
            tape: backup.numberTapesRequired,
            tapeCartridges: backup.numberTapeCartridgesRequired,
            cost: backup.backupCosts,
        });
    }

    return (
        <Table
            columnDefinitions={storageBackupSummaryTableCols(currency)}
            items={storageBackupSummaryItems}
            sortingDisabled
        />
    );
};

interface StorageOverHeadItem {
    overhead: string;
    quantity: number | null;
    cost: number | null;
}

const storageOverheadSummaryTableCols = (currency: string): TableProps.ColumnDefinition<StorageOverHeadItem>[] => [{
    id: 'type',
    header: constants.ON_PREM_OVERHEAD,
    cell: (item: StorageOverHeadItem) => (<Box float='left'><strong>{item.overhead}</strong></Box>),
}, {
    id: 'totalBackupSize',
    header: <Box float='right'>{constants.QUANTITY}</Box>,
    cell: (item: StorageOverHeadItem) => (
        <Box float='right'>{item.quantity ?? '-'}</Box>
    ),
}, {
    id: 'cost',
    header: <Box float='right'>{constants.COST}</Box>,
    cell: (item: StorageOverHeadItem) => (
        <Box float='right'>{item.cost != null ? formatCurrencyNoDecimals(item.cost, currency) : '-'}</Box>
    ),
}];

export const buildStorageOverheadSummaryTable = (storage: OnPremStorageOutput, currency: string): JSX.Element => {
    const storageOverheadItems: StorageOverHeadItem[] = [{
        overhead: constants.ON_PREM_RACK,
        quantity: storage.backupSummary.sharedStorageBackup?.numberRacksRequired ?? null,
        cost: storage.backupSummary.sharedStorageBackup?.rackOperations ?? null,
    }, {
        overhead: constants.SOFTWARE,
        quantity: null,
        cost: storage.backupSummary.sharedStorageBackup?.softwareCost ?? null,
    }];

    return (
        <Table
            columnDefinitions={storageOverheadSummaryTableCols(currency)}
            items={storageOverheadItems}
            sortingDisabled
        />
    );
};

interface NetworkSummaryItem {
    type: string;
    cost: number;
}

const networkSummaryTableCols = (currency: string): TableProps.ColumnDefinition<NetworkSummaryItem>[] => [{
    id: 'type',
    header: constants.TCO_PAGE_SUMMARY_TYPE,
    cell: (item: NetworkSummaryItem) => (<Box float='left'><strong>{item.type}</strong></Box>),
}, {
    id: 'cost',
    header: <Box float='right'>{constants.COST}</Box>,
    cell: (item: NetworkSummaryItem) => (
        <Box float='right'>{item.cost != null ? formatCurrencyNoDecimals(item.cost, currency) : '-'}</Box>
    ),
}];

export const buildNetworkSummaryTable = (network: OnPremNetworkOutput, currency: string): JSX.Element => {
    const networkItems: NetworkSummaryItem[] = [{
        type: constants.ON_PREM_NETWORK_HARDWARE_SOFTWARE,
        cost: network.networkCost,
    }, {
        type: constants.MAINTENANCE,
        cost: network.networkMaintenanceCost,
    }, {
        type: constants.BANDWIDTH,
        cost: network.bandwidthCost,
    }];

    return (
        <Table
            columnDefinitions={networkSummaryTableCols(currency)}
            items={networkItems}
            sortingDisabled
        />
    );
};

const serverStorageCols = (currency: string): ColumnDefinition<StorageVolume>[] => [
    {
        id: 'id',
        header: constants.SERVER_ID,
        cell: item => item.id,
        type: ColumnTypes.TEXT,
        text: constants.SERVER_ID,
    }, {
        id: 'volume',
        header: constants.VOLUME_GB,
        cell: item => getCell(`${item.volume} GB`, false),
        type: ColumnTypes.NUMBER,
        text: constants.VOLUME_GB,
    }, {
        id: 'cost',
        header: constants.COST,
        cell: item => getCell(formatCurrencyNoDecimals(item.cost, currency), false),
        type: ColumnTypes.NUMBER,
        text: constants.COST,
    },
];

export const buildServerStorageTable = (serverVolumes: StorageVolume[], currency: string): JSX.Element => {
    return (
        <AWSCATTable
            resourceName={constants.STORAGE_SERVER}
            items={serverVolumes}
            columnDefinitions={serverStorageCols(currency)}
            useDefaultDownload
            enableAutoSorting
            autoGenerateVisibleContent
            resizeable
        />
    );
};

type DatabaseVolumeItem = StorageVolume & { databaseIds: string[] };

const databaseStorageCols = (currency: string): ColumnDefinition<DatabaseVolumeItem>[] => {
    const dbStorageCols = serverStorageCols(currency);
    const dbIdsCol = {
        id: 'databaseIds',
        header: constants.DATABASE_ID,
        cell: (item: DatabaseVolumeItem) => item.databaseIds.join(','),
        type: ColumnTypes.TEXT,
        text: constants.DATABASE_ID,
    };
    dbStorageCols.splice(1, 0, dbIdsCol);
    return dbStorageCols;
};

export const buildDatabaseStorageTable = (databaseVolumes: DatabaseVolumeItem[], currency: string): JSX.Element => {
    return (
        <AWSCATTable
            resourceName={constants.STORAGE_DATABASE}
            items={databaseVolumes}
            columnDefinitions={databaseStorageCols(currency)}
            useDefaultDownload
            enableAutoSorting
            autoGenerateVisibleContent
            resizeable
        />
    );
};

type StorageCashFlowResources = {
    id: keyof StorageAnnualCost,
    label: string,
    unit?: string,
};
export const generateStorageCashFlowItems = (annualCosts: StorageAnnualCost[], annualTotalCosts: number[]): CashFlowData[] => {
    const storageCashFlowResources: StorageCashFlowResources[] = [
        {
            id: 'serverStorageSize',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_PHYSICAL_SERVER_SIZE,
            unit: 'GB',
        }, {
            id: 'serverStorageCost',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_PHYSICAL_SERVER_COST,
        }, {
            id: 'ServerSANSize',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_VIRTUAL_SERVER_SIZE,
            unit: 'GB',
        }, {
            id: 'ServerSANCost',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_VIRTUAL_SERVER_COST,
        }, {
            id: 'databaseStorageSize',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_PHYSICAL_DATABASE_SIZE,
            unit: 'GB',
        }, {
            id: 'databaseStorageCost',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_PHYSICAL_DATABASE_COST,
        }, {
            id: 'DatabaseSANSize',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_VIRTUAL_DATABASE_SIZE,
            unit: 'GB',
        }, {
            id: 'DatabaseSANCost',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_VIRTUAL_DATABASE_COST,
        }, {
            id: 'SANSize',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_SAN_SIZE,
            unit: 'GB',
        }, {
            id: 'SANCost',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_SAN_COST,
        }, {
            id: 'NASSize',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_NAS_SIZE,
            unit: 'GB',
        }, {
            id: 'NASCost',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_NAS_COST,
        }, {
            id: 'ObjectSize',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_OBJECT_SIZE,
            unit: 'GB',
        }, {
            id: 'ObjectCost',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_OBJECT_COST,
        }, {
            id: 'racksRequired',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_RACKS_REQUIRED,
            unit: '',
        }, {
            id: 'rackCost',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_RACKS_COST,
        }, {
            id: 'monthlyOperationCost',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_FACILITIES,
        }, {
            id: 'softwareCost',
            label: constants.SOFTWARE,
        }, {
            id: 'backupSize',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_BACKUP_SIZE,
            unit: 'GB',
        }, {
            id: 'tapeDriversRequired',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_TAPE_REQUIRED,
            unit: '',
        }, {
            id: 'tapeCartridgesRequired',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_TAP_CARTRDGES,
            unit: '',
        }, {
            id: 'backupCost',
            label: constants.ON_PREM_CASH_FLOW_STORAGE_BACKUP_COST,
        },
    ];

    const storageCashFlowItems: CashFlowData[] = [];
    storageCashFlowResources.forEach(resource => {
        storageCashFlowItems.push({ type: resource.label } as CashFlowData);
    });

    // Total cost cash flows
    storageCashFlowItems.push({ type: constants.TOTAL } as CashFlowData);
    const numYears = annualCosts.length;
    for (let yearIndex = 0; yearIndex < numYears; yearIndex++) {
        storageCashFlowResources.forEach((resource, resourceIndex) => {
            storageCashFlowItems[resourceIndex]['year' + yearIndex] = annualCosts[yearIndex][resource.id];
            storageCashFlowItems[resourceIndex].otherUnit = resource.unit;
        });
        storageCashFlowItems[storageCashFlowItems.length - 1]['year' + yearIndex] = annualTotalCosts[yearIndex];
    }

    return storageCashFlowItems;
};

export const generateNetworkCashFlowItems = (networkOutput: OnPremNetworkOutput): CashFlowData[] => {
    const networkCashFlow = {
        type: constants.ON_PREM_CASH_FLOW_NETWORK_COST,
    };
    const networkMaintenanceCashFlow = {
        type: constants.ON_PREM_CASH_FLOW_NETWORK_MAINTENANCE,
    };
    const bandwidthCashFlow = {
        type: constants.ON_PREM_CASH_FLOW_NETWORK_BANDWIDTH,
    };
    const redundancyCashFlow = {
        type: constants.ON_PREM_CASH_FLOW_NETWORK_REDUNDANCY,
    };
    const coloCashFlow = {
        type: constants.ON_PREM_CASH_FLOW_NETWORK_COLO_CHARGE,
    };
    const totalCashFlow = {
        type: constants.TOTAL,
    };
    const { yearly } = networkOutput.cashFlow;
    const numYears = yearly.total.length;
    
    for (let yearIndex = 0; yearIndex < numYears; yearIndex++) {
        (networkCashFlow as CashFlowData)['year' + yearIndex] = yearly.networkCost[yearIndex];
        (networkMaintenanceCashFlow as CashFlowData)['year' + yearIndex] = yearly.networkMaintenanceCost[yearIndex];
        (bandwidthCashFlow as CashFlowData)['year' + yearIndex] = yearly.bandwidthCost[yearIndex];
        (redundancyCashFlow as CashFlowData)['year' + yearIndex] = yearly.onPremRedundancy[yearIndex];
        (coloCashFlow as CashFlowData)['year' + yearIndex] = yearly.coloRecurringCharge[yearIndex];
        (totalCashFlow as CashFlowData)['year' + yearIndex] = yearly.total[yearIndex];
    }
    return [
        networkCashFlow as CashFlowData,
        networkMaintenanceCashFlow as CashFlowData,
        bandwidthCashFlow as CashFlowData,
        redundancyCashFlow as CashFlowData,
        coloCashFlow as CashFlowData,
        totalCashFlow as CashFlowData,
    ];
};
